import { ForemanDB } from '../../types/IndexedDB/ForemanDB'
import dayjs from 'dayjs'
import {
	ShiftEndTaskSummary,
	ShiftEndWorkItemsSummary,
} from '../../types/Reports/ShiftEnd/ShiftEndWorkItems'
import { Activity } from '../../types/Reports/ShiftEnd/Activity'
import Dexie from 'dexie'
import { WorkItemStatus } from '../../types/Work/WorkItemStatus'
import { WorkItemTask } from '../../types/Work/WorkItemTask'
import { getTransactionTables } from '../../utils/MutationSnapshotUtils'
import { TimeSheet } from '../../types/Reports/ShiftEnd/TimeSheet'
import { mergeConsecutiveDuplicates } from '../../utils/TimesheetUtils'

export const getTimeSheetsForSupervisorFB = async (db: ForemanDB, date: Date) => {
	// TODO: DONT FILTER BY DAY HERE. FILTER BY WHATS AVAILABLE ON CACHE.
	const timesheets = await db.timesheets
		.filter((t) => dayjs(t.localDate).isSame(date, 'day'))
		.toArray()
	const timeRecords = await db.timeRecords
		.filter((t) => timesheets.some((ts) => ts.id === t.timesheetId))
		.toArray()
	for (const item of timeRecords) {
		const hierarchy: string[] = []
		if (!item.workItemId) continue
		if (item.activity === 'Constraint') continue

		let workItem = await db.workItems.get(item.workItemId)
		while (workItem) {
			hierarchy.push(workItem.name)
			workItem = workItem.parentId ? await db.workItems.get(workItem.parentId) : undefined
		}
		hierarchy.reverse()
		const workItemPath = hierarchy.join(' > ') + ' > ' + item.activity
		item.type = workItemPath
	}
	timesheets.forEach((t) => {
		t.activities = timeRecords.filter((tr) => tr.timesheetId === t.id)
		t.activities.sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
	})

	return timesheets
}

export const getDailyActivitiesFB = async (db: ForemanDB) => {
	const timeRecords = await db.timeRecords.toArray()
	const taskIds = new Set(
		timeRecords
			.map((tr) => tr.workItemTaskId)
			.filter((id): id is number => id !== undefined && id !== null),
	)
	const workItemIds = new Set(
		timeRecords
			.filter((tr) => tr.workItemTaskId !== undefined)
			.map((tr) => tr.workItemId)
			.filter((id): id is number => id !== undefined && id !== null),
	)
	const workItemSummaries: ShiftEndWorkItemsSummary[] = []

	for (const id of Array.from(workItemIds)) {
		const hierarchy: string[] = []

		let workItem = await db.workItems.get(id)
		const tasks = await db.tasks.filter((t) => t.workItemId === id).sortBy('number')

		// Create task summaries without circular references
		const taskSummaries: ShiftEndTaskSummary[] = []
		for (let i = 0; i < tasks.length; i++) {
			const t = tasks[i]
			const maxQuantity =
				t.number === 1
					? (workItem?.quantityBudgeted ?? 0) - (t?.quantityComplete ?? 0)
					: i > 0
					? taskSummaries[i - 1]?.quantityComplete ?? 0
					: 0

			taskSummaries.push({
				id: t.id,
				title: t.name,
				type: 'Task',
				maxQuantity,
				percentComplete: t.percentComplete,
				burnedHours: t.burnedHours,
				status: t.status as WorkItemStatus,
				quantityComplete: t.quantityComplete,
			})
		}

		const summary: ShiftEndWorkItemsSummary = {
			workItemId: id,
			type: 'workItem',
			title: '',
			quantityBudgeted: workItem?.quantityBudgeted ?? 0,
			burnedHours: workItem?.burnedHours ?? 0,
			performanceFactor: workItem?.pf ?? 0,
			percentComplete: workItem?.percentComplete ?? 0,
			budgetedHours: workItem?.budgetedHours ?? 0,
			status: workItem?.status ?? 'Active',
			unitOfMeasure: workItem?.unitOfMeasure ?? '',
			buildSheetRequired: false,
			tasks: taskSummaries.filter((t) => taskIds.has(t.id)),
		}
		while (workItem) {
			hierarchy.push(workItem.name)
			workItem = workItem.parentId ? await db.workItems.get(workItem.parentId) : undefined
		}
		hierarchy.reverse()
		const workItemPath = hierarchy.join(' > ') + ' > ' + 'task'
		summary.title = workItemPath
		workItemSummaries.push(summary)
	}
	return workItemSummaries
}

export const updateTimeSheetActivitiesFB = async (
	db: ForemanDB,
	timesheetId: number,
	activities: Activity[],
) => {
	db.setNewLogId()
	return await db.transaction(
		'rw',
		getTransactionTables(db, [
			'timeRecords',
			'tasks',
			'workItems',
			'workItemAssignments',
			'taskAssignments',
			'reworkAssignments',
			'reworks',
		]),
		async () => {
			const tx = Dexie.currentTransaction
			if (!tx) {
				throw new Error('No active transaction found')
			}
			// Update each timeRecord individually to trigger hooks properly
			for (const activity of activities) {
				const processedActivity = {
					...activity,
					startTime: dayjs(activity.startTime.toISOString().replace('Z', '')).toDate(),
					endTime:
						activity.endTime && !isNaN(activity.endTime.getTime())
							? dayjs(activity.endTime.toISOString().replace('Z', '')).toDate()
							: null,
					timesheetId: timesheetId,
				}

				// Use put() instead of bulkPut() to trigger hooks
				await tx.table('timeRecords').put(processedActivity)
			}
			await tx.table('mutations').add({
				route: `/TimeSheets/${timesheetId}/TimeRecords`,
				method: 'PATCH',
				body: activities,
				title: `Updated time sheet activities`,
				createdAt: new Date(),
				id: db.getLastLogId(),
			})
		},
	)
}

export const confirmTimesheetFB = async (db: ForemanDB, timesheetId: number) => {
	db.setNewLogId()
	return await db.transaction(
		'rw',
		getTransactionTables(db, [
			'timeRecords',
			'tasks',
			'workItems',
			'workItemAssignments',
			'taskAssignments',
			'reworkAssignments',
			'reworks',
			'timesheets',
			'directReports',
			'constraints',
		]),
		async () => {
			const tx = Dexie.currentTransaction
			if (!tx) {
				throw new Error('No active transaction found')
			}
			const timesheet = (await tx.table('timesheets').get(timesheetId)) as TimeSheet
			if (!timesheet) {
				throw new Error('Timesheet not found')
			}

			await tx.table('timesheets').update(timesheetId, { confirmed: true })
			await tx.table('directReports').update(timesheet.workerId!, {
				activeTaskId: undefined,
				activeReworkId: undefined,
			})
			const activities = await tx
				.table('timeRecords')
				.filter((tr) => tr.timesheetId === timesheetId)
				.toArray()
			const tasks: WorkItemTask[] = await tx
				.table('tasks')
				.filter((t) => activities.some((a) => a.workItemTaskId === t.id))
				.toArray()
			const activitiesGroupedByTaskId = activities
				.filter((a) => a.workItemTaskId)
				.reduce((acc, activity) => {
					acc[activity.workItemTaskId!] =
						(acc[activity.workItemTaskId!] || 0) + (activity.hours ?? 0)
					return acc
				}, {} as Record<number, number>)
			// TODO: UPDATE REWORK Burned Hours too
			for (const taskId of Object.keys(activitiesGroupedByTaskId)) {
				const task = tasks.find((t) => t.id === parseInt(taskId))
				if (!task) continue

				await tx.table('tasks').update(parseInt(taskId), {
					burnedHours: (task.burnedHours ?? 0) + activitiesGroupedByTaskId[parseInt(taskId)],
					status: 'Inactive',
				})
				await db.triggerWorkItemStatusUpdate(task.workItemId!, tx)
			}
			for (const a of activities) {
				if (a.workItemId) {
					await db.calculateWorkItem(a.workItemId, tx)
				}
			}
			await tx.table('mutations').add({
				route: `/TimeSheets/${timesheetId}/Confirm`,
				method: 'PATCH',
				body: {},
				title: `Confirmed timesheet ${timesheet.title}`,
				createdAt: new Date(),
				id: db.getLastLogId(),
			})
		},
	)
}
export const removeRecordFB = async (db: ForemanDB, timesheetId: number, timeRecordId: number) => {
	db.setNewLogId()
	return await db.transaction(
		'rw',
		getTransactionTables(db, ['timeRecords', 'timesheets']),
		async () => {
			const tx = Dexie.currentTransaction
			if (!tx) {
				throw new Error('No active transaction found')
			}
			const timeSheet = await tx.table('timesheets').get(timesheetId)
			if (!timeSheet) {
				throw new Error('Time sheet not found')
			}
			let timeRecords = (await tx
				.table('timeRecords')
				.filter((tr) => tr.timesheetId === timesheetId)
				.sortBy('startTime')) as Activity[]
			const recordIdx = timeRecords.findIndex((tr) => tr.id === timeRecordId)
			if (recordIdx === 0 && timeRecords.length === 1) {
				await tx.table('timeRecords').delete(timeRecordId)
				await tx.table('timesheets').delete(timesheetId)
				return
			}
			if (recordIdx !== 0) {
				timeRecords[recordIdx - 1].endTime = timeRecords[recordIdx].endTime
				
				timeRecords.splice(recordIdx, 1)
				timeRecords = mergeConsecutiveDuplicates(timeRecords)
				await tx.table('timeRecords').bulkPut(timeRecords)
			}
			await tx.table('timeRecords').delete(timeRecordId)

			await tx.table('mutations').add({
				route: `/TimeSheets/${timesheetId}/TimeRecords/${timeRecordId}`,
				method: 'DELETE',
				body: {},
				title: `Removed time record ${timeRecordId} from timesheet ${timesheetId}`,
				createdAt: new Date(),
				id: db.getLastLogId(),
			})
		},
	)
}

export const updateTimeRecordCommentFB = async (
	db: ForemanDB,
	timesheetId: number,
	timeRecordId: number,
	comment: string,
) => {
	db.setNewLogId()
	return await db.transaction('rw', getTransactionTables(db, ['timeRecords']), async () => {
		const tx = Dexie.currentTransaction
		if (!tx) {
			throw new Error('No active transaction found')
		}
		const timeRecord = await tx.table('timeRecords').get(timeRecordId)
		if (!timeRecord || timeRecord.timesheetId !== timesheetId) {
			throw new Error('Time record not found')
		}
		await tx.table('timeRecords').update(timeRecordId, { comment })
		await tx.table('mutations').add({
			route: `/TimeSheets/${timesheetId}/TimeRecords/${timeRecordId}/Comment`,
			method: 'PATCH',
			body: { comment },
			title: `Updated comment on time record ${timeRecordId}`,
			createdAt: new Date(),
			id: db.getLastLogId(),
		})
	})
}
